import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Smartphone, X } from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Banner informativo: recuerda al hostelero/administrador que los negocios con
 * plan de pago activo o en periodo de prueba aparecen automáticamente en la
 * app de clientes de ReservaManager (búsqueda por cercanía + reserva directa).
 *
 * Detección de "aparece en la app" (aproximación en frontend):
 * el backend (`GET /discover/nearby`, `reservamanager-backend/src/modules/
 * discover/controller.ts`) exige `showInApp=true` en la ubicación Y
 * (`subscriptionStatus="active"` con plan Pro/Ultimate) O
 * (`subscriptionStatus="trialing"` con `trialEndsAt` futuro). El dashboard no
 * tiene una llamada dedicada que devuelva ese booleano ya resuelto por
 * ubicación, así que se aproxima con la misma señal que ya usa
 * `TrialBanner` (`useSubscription().features`): `isInTrial` (coincide 1:1 con
 * la condición de trialing del backend) o plan de pago activo
 * (`isActive && planName !== "Free"`, aproximación razonable — hoy el
 * backend solo admite Pro/Plan Ultimate en el descubrimiento, pero cualquier
 * plan que no sea Free implica intención de pago). No refleja el toggle
 * `showInApp` por ubicación (eso ya lo comunica el propio interruptor en
 * Ubicaciones), solo si el PLAN del negocio lo permite.
 *
 * No se muestra a la vez que `TrialBanner` para no saturar la cabecera del
 * dashboard con banners apilados: si el negocio está en prueba, ya lo cubre
 * TrialBanner (con su propio CTA a "Ver planes"), así que este banner cede el
 * turno y solo aparece para plan de pago activo o plan Free.
 */
export default function AppDiscoveryBanner() {
  const { features, isLoading } = useSubscription();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);

  const isAdmin = user?.role === "admin" || user?.role === "owner";
  const dismissKey = "app_discovery_banner_dismissed";

  const alreadyDismissed =
    typeof window !== "undefined" &&
    localStorage.getItem(dismissKey) === "1";

  if (
    isLoading ||
    !features ||
    !isAdmin ||
    dismissed ||
    alreadyDismissed ||
    location.pathname === "/subscription" ||
    location.pathname === "/locations" ||
    location.pathname === "/location-settings"
  ) {
    return null;
  }

  // Ceder el turno a TrialBanner mientras la prueba está activa (evita
  // apilar dos banners de conversión en la misma pantalla).
  if (features.isInTrial) {
    return null;
  }

  const appearsInApp = features.isActive && features.planName !== "Free";

  const handleDismiss = () => {
    localStorage.setItem(dismissKey, "1");
    setDismissed(true);
  };

  const goToLocations = () => navigate("/locations");
  const goToSubscription = () => navigate("/subscription");

  return (
    <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border px-4 py-3 bg-sky-50 border-sky-200 text-sky-900">
      <div className="flex items-start gap-2 flex-1">
        <Smartphone className="h-5 w-5 mt-0.5 shrink-0" />
        <p className="text-sm">
          {appearsInApp ? (
            <>
              <strong>Ya apareces en la app ReservaManager:</strong> tus
              clientes te encuentran cerca y reservan solos.
            </>
          ) : (
            <>
              Tus futuros clientes te buscan en la app.{" "}
              <strong>Pásate a Pro y aparece</strong> para que te encuentren.
            </>
          )}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={appearsInApp ? goToLocations : goToSubscription}
          className="rounded-lg px-4 py-2 text-sm font-medium text-white bg-sky-600 hover:bg-sky-700"
        >
          {appearsInApp ? "Ver \"Visible en la app\"" : "Ver planes"}
        </button>
        <button
          onClick={handleDismiss}
          className="p-1.5 rounded hover:bg-black/5"
          title="Ocultar aviso"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
